import { DataTable } from '@/components'
import { useTranslate } from '@ur-solutions/react-hooks'
import { createColumnHelper } from '@tanstack/react-table'
import type { ResultOf } from 'gql.tada'
import { useMemo } from 'react'
import { useSearchParams } from 'react-router'
import { GradePill, InfoLabel } from './grade-parts'
import { formatNumber } from './metrics'
import type { DRIVER_GRADE_MONTH_QUERY } from './queries'

type MonthRow = ResultOf<
  typeof DRIVER_GRADE_MONTH_QUERY
>['driverGradeMonth']['rows'][number]

interface TableRow {
  id: string
  rank: number
  userId: string
  name: string
  grade: number | null
  days: number
  distanceKm: number
  idleShare: number | null
  cruiseShare: number | null
  bestGrade: number
  worstGrade: number
  pointsToGain: number
}

const metricValue = (row: MonthRow, key: string) =>
  row.metrics.find(metric => metric.key === key)?.value ?? null

const DriverLink: React.FC<{ userId: string; name: string }> = ({
  userId,
  name,
}) => {
  const [params, setParams] = useSearchParams()

  return (
    <button
      type="button"
      className="text-left font-medium text-stone-900 underline-offset-2 hover:underline"
      onClick={() => {
        const next = new URLSearchParams(params)
        next.set('user', userId)
        next.delete('day')
        setParams(next)
      }}
    >
      {name}
    </button>
  )
}

const percent = (value: number | null) =>
  value === null ? '–' : `${formatNumber(value)} %`

export const DriverGradeMonthTable: React.FC<{
  rows: MonthRow[]
  loading: boolean
}> = ({ rows, loading }) => {
  const translations = useTranslate({
    driver: 'common.driver',
    grade: 'driver-grade.grade',
    gradeInfo: 'driver-grade.info-month',
    days: 'driver-grade.graded-days',
    distance: 'common.distance',
    idle: 'driver-grade.metric-idle_share',
    cruise: 'driver-grade.metric-cruise_share',
    bestWorst: 'driver-grade.best-worst',
    toPerfect: 'driver-grade.to-perfect',
    noGrades: 'driver-grade.no-grades',
  })

  const data = useMemo<TableRow[]>(
    () =>
      rows.map((row, index) => ({
        id: row.user.id,
        rank: index + 1,
        userId: row.user.id,
        name: row.user.fullName,
        grade: row.grade ?? null,
        days: row.days,
        distanceKm: row.distanceKm,
        idleShare: metricValue(row, 'idle_share'),
        cruiseShare: metricValue(row, 'cruise_share'),
        bestGrade: row.bestGrade,
        worstGrade: row.worstGrade,
        pointsToGain: row.metrics.reduce(
          (sum, metric) => sum + metric.pointsToGain,
          0
        ),
      })),
    [rows]
  )

  const {
    driver,
    grade,
    gradeInfo,
    days,
    distance,
    idle,
    cruise,
    bestWorst,
    toPerfect,
  } = translations

  const columns = useMemo(() => {
    const column = createColumnHelper<TableRow>()
    return [
      column.accessor('rank', {
        header: '#',
        enableSorting: true,
        meta: { narrow: true },
        cell: info => (
          <span className="tabular-nums text-stone-500">{info.getValue()}</span>
        ),
      }),
      column.accessor('name', {
        header: driver,
        enableSorting: true,
        cell: info => (
          <DriverLink
            userId={info.row.original.userId}
            name={info.getValue()}
          />
        ),
      }),
      column.accessor('grade', {
        header: () => <InfoLabel plain label={grade} info={gradeInfo} />,
        enableSorting: true,
        sortUndefined: 'last',
        cell: info => <GradePill grade={info.getValue()} />,
      }),
      column.accessor('days', {
        header: days,
        enableSorting: true,
        meta: { end: true },
        cell: info => <span className="tabular-nums">{info.getValue()}</span>,
      }),
      column.accessor('distanceKm', {
        header: distance,
        enableSorting: true,
        meta: { end: true },
        cell: info => (
          <span className="tabular-nums">
            {formatNumber(info.getValue(), 0)} km
          </span>
        ),
      }),
      column.accessor('idleShare', {
        header: idle,
        enableSorting: true,
        meta: { end: true },
        cell: info => (
          <span className="tabular-nums">{percent(info.getValue())}</span>
        ),
      }),
      column.accessor('cruiseShare', {
        header: cruise,
        enableSorting: true,
        meta: { end: true },
        cell: info => (
          <span className="tabular-nums">{percent(info.getValue())}</span>
        ),
      }),
      column.display({
        id: 'bestWorst',
        header: bestWorst,
        meta: { end: true },
        cell: info => (
          <span className="tabular-nums text-stone-600">
            {formatNumber(info.row.original.bestGrade)} /{' '}
            {formatNumber(info.row.original.worstGrade)}
          </span>
        ),
      }),
      column.accessor('pointsToGain', {
        header: toPerfect,
        enableSorting: true,
        meta: { end: true },
        cell: info => (
          <span className="tabular-nums text-emerald-700">
            {formatNumber(info.getValue())}
          </span>
        ),
      }),
    ]
  }, [
    bestWorst,
    cruise,
    days,
    distance,
    driver,
    grade,
    gradeInfo,
    idle,
    toPerfect,
  ])

  return (
    <div className="overflow-x-auto">
      <DataTable
        inCard
        columns={columns}
        data={data}
        loading={loading}
        noDataText={translations.noGrades}
      />
    </div>
  )
}
